import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import initialStyles from '../settings/initialStyles';
import { linkIcons, linkText } from '../settings/linkData';
import ThemeConfigPanel from '../settings/ThemeConfigPanel';
import Notification from '../components/notification/Notification';

import 'react-circular-progressbar/dist/styles.css';
import '../styles/TemplateNine.css';

const themePresets = {
    default: initialStyles,
    dark: {
        backgroundColor: '#333',
        secondaryBackgroundColor: '#555',
        headerBackgroundColor: '#222',
        headerTextColor: '#fff',
        subheaderTextColor: '#555',
        textColor: '#eee',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    },
    light: {
        backgroundColor: '#f9f9f9',
        secondaryBackgroundColor: '#e0e0e0',
        headerBackgroundColor: '#ddd',
        headerTextColor: '#333',
        subheaderTextColor: '#555',
        textColor: '#111',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    }
};

const ViewTemplateNine = ({ template, isDownloading }) => {
    const [styles, setStyles] = useState(initialStyles);
    const [notification, setNotification] = useState(null);

    const handleStyleChange = (e) => {
        const { name, value } = e.target;
        setStyles((prevStyles) => ({
            ...prevStyles,
            [name]: value,
        }));
    };

    const applyPreset = (preset) => {
        setStyles(themePresets[preset]);
    };

    const saveCustomTheme = () => {
        localStorage.setItem('customTheme', JSON.stringify(styles));
        setNotification({ message: 'Custom theme saved!', isError: false });
    };

    const loadCustomTheme = () => {
        const customTheme = localStorage.getItem('customTheme');
        if (customTheme) {
            setStyles(JSON.parse(customTheme));
            setNotification({ message: 'Custom theme loaded!', isError: false });
        } else {
            setNotification({ message: 'No custom theme found!', isError: true });
        }
    };

    const templateData = template || {};

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://venusresume.vercel.app/';
    const imageUrl = templateData?.image?.startsWith('/')
        ? `${baseUrl}${templateData.image}`
        : templateData.image;

    return (
        <div className='holder'>

            {notification && (
                <Notification
                    message={notification.message}
                    isError={notification.isError}
                    onClose={() => setNotification(null)}
                />
            )}
            <div className="template-nine" style={{ backgroundColor: styles.backgroundColor }}>
                <div className="template-nine-header" style={{ backgroundColor: styles.headerBackgroundColor }}>
                    <div className="template-nine-image-container">
                        {imageUrl && <img src={imageUrl} alt={template?.personName} className="template-nine-image" />}
                    </div>
                    <div className="template-nine-name-title">
                        {template?.personName && <h1 className="template-nine-name" style={{ fontSize: `${styles.headerFontSize}em`, color: styles.headerTextColor }}>{template?.personName}</h1>}
                    </div>
                </div>

                <div className="template-nine-body">
                    <div className="template-nine-columns">
                        <div className="template-nine-column-left" style={{ backgroundColor: styles.secondaryBackgroundColor, padding: "12px" }}>

                            {templateData?.contact && (
                                <div className="template-nine-contact template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Contact</h3>
                                    {templateData.contact.phone && <p><FontAwesomeIcon icon={faPhone} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.phone}</p>}
                                    {templateData.contact.email && <p><FontAwesomeIcon icon={faEnvelope} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.email}</p>}
                                    {templateData.contact.address && <p><FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.address}</p>}
                                </div>
                            )}

                            {templateData?.skills && templateData.skills.length > 0 && (
                                <div className="template-nine-skills template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Skills</h3>
                                    <ul>
                                        {templateData.skills.map((skill, index) => (
                                            <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {templateData?.softwareSkills && templateData.softwareSkills.length > 0 && (
                                <div className="template-nine-software-skills template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Software Skills</h3>
                                    <ul>
                                        {templateData.softwareSkills.map((skill, index) => (
                                            <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {templateData?.languages && (
                                <div className="template-nine-languages template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Languages</h3>
                                    <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{templateData.languages.join(', ')}</p>
                                </div>
                            )}
                        </div>

                        <div className="template-nine-column-right">
                            {templateData?.aboutMe && (
                                <div className="template-nine-about-me template-nine-section template-nine-education-item">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>About Me</h3>
                                    <p style={{ fontSize: `${styles.bodyFontSize}em`, color: styles.textColor }}>{templateData.aboutMe}</p>
                                </div>
                            )}

                            {templateData?.workExperience && templateData.workExperience.length > 0 && (
                                <div className="template-nine-experience template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Experience</h3>
                                    {templateData.workExperience.map((experience, index) => (
                                        <div key={index} className="template-nine-experience-item">
                                            <p className="template-nine-role" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.role}</p>
                                            <p className="template-nine-company" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.company}</p>
                                            <p className="template-nine-description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.description}</p>
                                            {experience.location && (
                                                <p className="location" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                                    {experience.location} (
                                                    {experience.startDate && new Date(experience.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                                    {experience.endDate !== "Present" ? new Date(experience.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                                    )
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {templateData?.education && templateData.education.length > 0 && (
                                <div className="template-nine-education template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Education</h3>
                                    {templateData.education.map((edu, index) => (
                                        <div key={index} className="template-nine-education-item">
                                            <p className="template-nine-degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.degree}</p>
                                            <p className="template-nine-institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.institution}</p>
                                            <p style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "200" }}>
                                                (
                                                {edu.startDate && new Date(edu.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                                {edu.endDate !== "Present" ? new Date(edu.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                                )
                                            </p>
                                            <p className="template-nine-institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.description}</p>

                                        </div>
                                    ))}
                                </div>
                            )}

                            {templateData?.certificates && templateData.certificates.length > 0 && (
                                <div className="template-nine-certificates template-nine-section">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Certificates</h3>
                                    {templateData.certificates.map((certification, index) => (
                                        <div key={index} className="template-nine-education-item">
                                            <p className="template-nine-degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>Title: {certification.title}</p>
                                            <p className="template-nine-issuer" style={{ fontSize: `${styles.bodyFontSize}em` }}>Institution: {certification.institution}</p>
                                            <p className="template-nine-date" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                                Date Received: {new Date(certification.DateReceived).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {templateData?.hobbies && (
                                <div className="template-nine-hobbies template-nine-section template-nine-education-item">
                                    <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Hobbies</h3>
                                    <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{templateData.hobbies.join(', ')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="template-nine-footer">
                        {templateData?.links && (
                            <div className="template-nine-links">
                                <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Links</h3>
                                <div className='template-nine-links-place'>
                                    {Object.entries(templateData.links).map(([key, value]) => (
                                        <p key={key} >
                                            <FontAwesomeIcon icon={linkIcons[key] || faLink} /> <a href={value} target="_blank" rel="noopener noreferrer" style={{ fontSize: `${styles.bodyFontSize}em` }}>{linkText[key]}</a>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!isDownloading && (
                <ThemeConfigPanel
                    styles={styles}
                    handleStyleChange={handleStyleChange}
                    applyPreset={applyPreset}
                    saveCustomTheme={saveCustomTheme}
                    loadCustomTheme={loadCustomTheme}
                />
            )}
        </div>
    );
};

export default ViewTemplateNine;
